import { z } from "zod";

import { SparkChartRow } from "@/components/ui/spark-chart";

const sparkChartRowItemSchema = z.object({
  name: z.string(),
  description: z.string().nullable().optional(),
  category: z.string(),
  change: z.string().nullable().optional(),
  changeType: z.enum(["positive", "negative", "neutral"]).nullable().optional(),
});

export const ChatSparkChartRowSpec = {
  props: z.object({
    items: z.array(sparkChartRowItemSchema),
    data: z.array(z.record(z.string(), z.union([z.string(), z.number()]))),
    index: z.string(),
    className: z.string().optional(),
  }),
  description:
    "Compact spark chart row: a grid of cards, each with a name, optional description, optional change badge (e.g. +4.1%, -3.2%, +2.0pp), and a small area spark chart. Use for trend-focused time-series comparisons of 2–4 KPIs side by side (e.g. totalArticles, avgImpactScore, positiveShare, engagement). Do not use for composition-only snapshots; use HorizontalBar or PieChart for those. Each item needs name, category (key in data for the series), and optional description/change/changeType. data is an array of points; every point must include the index key (e.g. date/period) and a numeric value for each item's category. Keep all series on the same time grain and same points. changeType 'positive' = green badge, 'negative' = rose badge, 'neutral' = gray badge. For clean layout: use short names (e.g. 'Total articles'); put longer text in description.",
  example: {
    items: [
      {
        name: "Total articles",
        description: "Mentions across selected feeds",
        category: "totalArticles",
        change: "+12.4%",
        changeType: "positive",
      },
      {
        name: "Avg impact",
        description: "Average impact score",
        category: "avgImpactScore",
        change: "-3.1%",
        changeType: "negative",
      },
      {
        name: "Positive share",
        description: "Share of positive sentiment",
        category: "positiveShare",
        change: "+2.0pp",
        changeType: "positive",
      },
    ],
    data: [
      {
        date: "Mar 20",
        totalArticles: 120,
        avgImpactScore: 6.2,
        positiveShare: 42.1,
      },
      {
        date: "Mar 21",
        totalArticles: 134,
        avgImpactScore: 6.0,
        positiveShare: 43.8,
      },
      {
        date: "Mar 22",
        totalArticles: 128,
        avgImpactScore: 6.1,
        positiveShare: 44.2,
      },
      {
        date: "Mar 23",
        totalArticles: 142,
        avgImpactScore: 6.3,
        positiveShare: 43.4,
      },
      {
        date: "Mar 24",
        totalArticles: 149,
        avgImpactScore: 6.4,
        positiveShare: 44.1,
      },
    ],
    index: "date",
  },
} as const;

/** Forwards props to {@link SparkChartRow}. */
export const ChatSparkChartRowFromProps = ({
  props,
}: {
  props: Record<string, unknown>;
}) => {
  const parsed = ChatSparkChartRowSpec.props.safeParse(props);
  if (!parsed.success) return null;
  const { items, data, index, className } = parsed.data;
  return (
    <div className="w-full max-w-4xl">
      <SparkChartRow
        items={items}
        index={index}
        data={data}
        className={className}
      />
    </div>
  );
};
